// useClusterData — single source of truth for liquidation cluster values
// exposed by the Liquidity Map backend.
//
// Why this hook exists:
//   The Liquidity Map renders the same "max cluster" value in 4+ places at
//   once (header stat, cascade banner card, canvas MAX label, arrow overlay,
//   IA payload, validation toast). Before this hook, several of those
//   consumers read snapshot.stats.cluster_max_price directly while one (the
//   cascade banner card) used a pinned snapshot (`cascadeData._evaluated_at
//   .cluster_max`) that could be up to 5 minutes stale — producing 0.04–0.16%
//   drift between displays for the same conceptual value.
//
//   Centralising the read through this hook does two things:
//     1) Makes the invariant "all displays read the SAME cluster value" an
//        explicit, mechanically enforceable contract.
//     2) Prevents future contributors from re-introducing a pinned/cached
//        cluster value silently.
//
// Returns a memoised object so reference identity is stable while the
// underlying snapshot doesn't change — safe to use as a dependency in
// downstream useMemo / useEffect.
import { useMemo } from "react";

export const useClusterData = (snapshot) => {
  return useMemo(() => {
    const s = snapshot?.stats || {};
    const num = (v) => (typeof v === "number" ? v : null);
    return {
      // Primary (MAX) cluster — most relevant long-liquidation zone above
      // the current price, or short-liquidation zone below it, depending on
      // the backend's classification.
      maxPrice:        num(s.cluster_max_price),
      maxConfidence:   num(s.cluster_max_confidence) ?? 0,
      maxIntensity:    num(s.cluster_max_intensity) ?? 0.7,
      maxTipo:         s.cluster_max_tipo || "",
      // Secondary (SUP/support) cluster — opposite-side zone.
      supPrice:        num(s.cluster_second_price),
      supConfidence:   num(s.cluster_sup_confidence) ?? 0,
      supTipo:         s.cluster_sup_tipo || "",
    };
  }, [snapshot]);
};

export default useClusterData;
